import type { JSX } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ExperienceList,
  type ExperienceListProps,
} from '@/features/experiences/components/ExperienceList';
import { ExperienceFormDialog } from '@/features/experiences/components/ExperienceFormDialog';
import { ExperienceVerificationsSection } from './ExperienceVerificationsSection';
import { useCandidateExperiences } from '../hooks/useCandidateExperiences';

interface CandidateExperiencesSectionProps {
  candidateId: string;
}

interface ExperienceListCardProps extends Omit<ExperienceListProps, 'renderExpandedContent'> {
  candidateId: string;
  onAddClick: () => void;
}

/** Each row expands into its own verifications section, so the card owns that wiring. */
function ExperienceListCard({
  candidateId,
  onAddClick,
  ...listProps
}: ExperienceListCardProps): React.JSX.Element {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle>
          <h2 className="text-lg font-semibold">Experiences</h2>
        </CardTitle>
        <Button onClick={onAddClick}>
          <Plus className="mr-2 h-4 w-4" />
          Add Experience
        </Button>
      </CardHeader>
      <CardContent>
        <ExperienceList
          {...listProps}
          renderExpandedContent={(experience) => (
            <ExperienceVerificationsSection
              candidateId={candidateId}
              experienceId={experience.id}
            />
          )}
        />
      </CardContent>
    </Card>
  );
}

export function CandidateExperiencesSection({
  candidateId,
}: CandidateExperiencesSectionProps): JSX.Element {
  const {
    experiences,
    hasData,
    isLoading,
    isError,
    isCreateDialogOpen,
    setIsCreateDialogOpen,
    editingExperience,
    setEditingExperience,
    handleDelete,
    handleStatusChange,
  } = useCandidateExperiences(candidateId);

  return (
    <>
      <ExperienceListCard
        candidateId={candidateId}
        experiences={experiences}
        hasData={hasData}
        isLoading={isLoading}
        isError={isError}
        onEdit={setEditingExperience}
        onDelete={handleDelete}
        onStatusChange={handleStatusChange}
        onAddClick={() => setIsCreateDialogOpen(true)}
      />

      <ExperienceFormDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        mode="create"
        candidateId={candidateId}
      />

      <ExperienceFormDialog
        open={!!editingExperience}
        onOpenChange={(open) => !open && setEditingExperience(null)}
        mode="edit"
        candidateId={candidateId}
        experience={editingExperience}
      />
    </>
  );
}
